import { createDocument, createTheme, p, HeadingLevel } from '@skills/docx-export.jsx';
import { logoBlock } from '@generated/components/logoDocx';
import { cleanLink, dayHeading } from '@generated/components/sceneGuideDocx';
import type { getCommunityCalendar } from '@generated/server/communityCalendar';

export type CommunityCal = Awaited<ReturnType<typeof getCommunityCalendar>>;
export type CalItem = CommunityCal['events'][number];

export const placeLine = (e: CalItem) => [e.time, e.venue].filter(Boolean).join(' · ');

/** Community Calendar as a Word file for the InDesign pasteboard. */
export function buildCommunityDocx(c: CommunityCal, paperName: string, title: string) {
  const t = createTheme({ primary: '1F2937', secondary: 'B91C1C', accent: '1D4ED8', neutral: '222222', font: 'Georgia' });
  const k: unknown[] = [...logoBlock(), p('Community Calendar', { heading: HeadingLevel.TITLE }), p(paperName, { italic: true }), p(title, { bold: true })];
  const item = (e: CalItem) => {
    k.push(p(e.neighborhood.toUpperCase(), { size: 14, bold: true, color: 'B91C1C' }));
    k.push(p(e.title, { heading: HeadingLevel.HEADING_2 }));
    const line = placeLine(e); if (line) k.push(p(line, { bold: true }));
    if (e.cost) k.push(p(e.cost, { italic: true }));
    if (e.excerpt) k.push(p(`“${e.excerpt}”`));
    k.push(p(`Presented by ${e.org}${e.link?.startsWith('http') ? ` · ${cleanLink(e.link)}` : ''}`, { size: 16, color: '555555' }));
  };
  let day = '';
  c.events.forEach((e) => { const h = dayHeading(e.startsAt); if (h !== day) { day = h; k.push(p(h, { heading: HeadingLevel.HEADING_1 })); } item(e); });
  if (!c.events.length) k.push(p('No dated community events were listed for these dates.', { italic: true }));
  if (c.traditions.length) { k.push(p('Standing Weekly Community Traditions', { heading: HeadingLevel.HEADING_1 })); c.traditions.forEach((e) => { item(e); if (e.when) k.push(p(e.when, { size: 16, italic: true })); }); }
  k.push(p('Entertainment Calendar', { heading: HeadingLevel.TITLE }), p('Music · Comedy · Theater · Dance · Film', { italic: true }));
  let eday = '';
  (c.entertainment ?? []).forEach((e) => { const h = dayHeading(e.startsAt); if (h !== eday) { eday = h; k.push(p(h, { heading: HeadingLevel.HEADING_1 })); } item(e); });
  if (!(c.entertainment ?? []).length) k.push(p('No dated shows were listed for these dates.', { italic: true }));
  return createDocument({ title: `Community Calendar · ${title}`, headerText: `${paperName} · Community Calendar · ${title}`, styles: t.styles, numbering: t.numbering, children: k });
}
